import React, { useEffect, useState } from 'react'
import { Card, DonutChart, Text } from '@tremor/react'
import GetAppIcon from '@material-ui/icons/GetApp'
import { CircularProgress, IconButton } from '@material-ui/core'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'
import { formatDonutChartData } from 'utils/chart'
import CustomTooltip from 'features/Main/NewDashboard/components/CustomTooltip'

import { useLoadingTracker } from '../../../hooks/useLoadingTracker'
import { getAgeAnalysisByLevel, AgeAnalysisByLevelData } from './api'

interface AgeDonutChartProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period: { month: number; year: number }
  // eslint-disable-next-line react/require-default-props
  storeId?: number
  trigger: number
  compara?: boolean
  compareStoreId?: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const ageColors = ['#26342E', '#809E82', '#C9D296', '#F29569', '#E8B342', '#6B8F71']
const emptyDataColor = ['gray']
const montserratStyle = { fontFamily: 'Montserrat, sans-serif', color: '#1E1E1E' }

const arrowUp = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 10V2M6 2L2 6M6 2L10 6"
      stroke="#3A5A40"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const arrowDown = (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 2V10M6 10L2 6M6 10L10 6"
      stroke="#B91C1C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const AgeDonutChart: React.FC<AgeDonutChartProps> = ({
  period,
  storeId,
  trigger,
  compara = false,
  compareStoreId,
  onLoaded,
}) => {
  const [data, setData] = useState<AgeAnalysisByLevelData | null>(null)
  const [compareData, setCompareData] = useState<AgeAnalysisByLevelData | null>(null)
  const [statsCall, statsLoading] = useApiCall<
    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
    { startDate: string; endDate: string; storeId?: number },
    AgeAnalysisByLevelData
  >(getAgeAnalysisByLevel)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'AgeDonutChart' })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const startDate = moment()
          .year(period.year)
          .month(period.month)
          .startOf('month')
          .format('YYYY-MM-DD')
        const endDate = moment()
          .year(period.year)
          .month(period.month)
          .endOf('month')
          .format('YYYY-MM-DD')
        const response = await statsCall({ startDate, endDate, storeId })
        setData(response)
        if (compara && compareStoreId) {
          const compareResponse = await statsCall({
            startDate,
            endDate,
            storeId: compareStoreId,
          })
          setCompareData(compareResponse)
        } else {
          setCompareData(null)
        }
      } finally {
        if (onLoaded) {
          onLoaded()
        }
      }
    }
    if (trigger > 0) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, compara, compareStoreId])

  if (statsLoading || !data) {
    return (
      <div style={montserratStyle}>
        {statsLoading ? (
          <CircularProgress size={40} />
        ) : (
          <Text>No hay datos disponibles para mostrar en este momento.</Text>
        )}
      </div>
    )
  }

  const compareMap = compareData ? Object.fromEntries(compareData.data.map(i => [i.code, i])) : {}

  // Ordenar segmentos de mayor a menor según cantidad
  const orderedData = [...data.data].sort((a, b) => b.value - a.value)

  const isEmptyData =
    orderedData.length === 1 && orderedData[0].value === 0 && orderedData[0].code === 'empty'
  const colorsToUse = isEmptyData ? emptyDataColor : ageColors

  const donutData = formatDonutChartData(
    orderedData.map((item, idx) => ({
      name: item.name,
      value: item.value,
      color: colorsToUse[idx % colorsToUse.length],
    })),
  )
  const legendRows = orderedData.map((item, idx) => {
    const compareItem = compareMap[item.code]
    let diff = 0
    let isPositive = true
    let diffLabel = null
    if (compareItem) {
      diff = (item.averageFrequency ?? 0) - (compareItem.averageFrequency ?? 0)
      isPositive = diff >= 0
      diffLabel = (diff > 0 ? '+' : '') + diff.toFixed(2)
    }
    return {
      name: item.name,
      color: colorsToUse[idx % colorsToUse.length],
      value: item.proportion,
      frequency: item.averageFrequency,
      diff: diffLabel,
      isPositive,
      showComparison: !!compareItem,
    }
  })

  const downloadAgeCSV = (orderedDataCSV: typeof orderedData) => {
    const rows = [
      ['Categoría', 'Proporción (%)', 'Frecuencia promedio'],
      ...orderedDataCSV.map(row => [
        row.name,
        row.proportion,
        row.averageFrequency?.toFixed(2) ?? '-',
      ]),
    ]
    const csvContent = rows.map(e => e.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'edad.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Card className="p-6 bg-white" style={{ ...montserratStyle, minWidth: 380 }}>
      <div className="flex flex-row justify-between items-center mb-4">
        <div className="text-lg font-bold" style={montserratStyle}>
          Edad
        </div>
        <IconButton
          disabled={statsLoading}
          className="ml-2"
          aria-label="download"
          onClick={() => downloadAgeCSV(orderedData)}
        >
          <GetAppIcon />
        </IconButton>
      </div>
      <div className="flex flex-col items-center">
        <DonutChart
          data={donutData}
          category="value"
          index="name"
          colors={donutData.map(d => d.color)}
          showLabel={false}
          className="w-60 h-60"
          noDataText="No hay datos disponibles para mostrar en este momento."
          customTooltip={CustomTooltip}
        />
        {!isEmptyData && (
          <div className="w-full mt-8">
            <div className="grid grid-cols-12 text-sm font-semibold mb-2" style={montserratStyle}>
              <div className="col-span-5">Categorías</div>
              <div className="col-span-3 text-center">Cantidad</div>
              <div className="col-span-4 text-center">Frecuencia</div>
            </div>
            {legendRows.map(row => (
              <div
                key={row.name}
                className="grid grid-cols-12 items-center py-2"
                style={montserratStyle}
              >
                <div className="col-span-5 flex items-center gap-2">
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      background: row.color,
                      borderRadius: 4,
                      display: 'inline-block',
                    }}
                  />
                  <span>{row.name}</span>
                </div>
                <div className="col-span-3 text-center font-bold">{row.value} %</div>
                <div className="col-span-4 text-center flex items-center justify-center gap-2">
                  {row.frequency?.toFixed(2) ?? '-'}
                  {row.showComparison && row.diff && (
                    <span
                      className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                        row.isPositive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                      style={{ display: 'flex', alignItems: 'center', gap: 2 }}
                    >
                      {row.isPositive ? arrowUp : arrowDown}
                      <span>{row.diff}</span>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}

AgeDonutChart.defaultProps = {
  compara: false,
  compareStoreId: undefined,
  storeId: undefined,
}

export default AgeDonutChart
